/**
 * PR-D4-031 — MathTestPage uses the same MadCrab SatTest chrome as Verbal.
 * PR-D4-062 — Universal Bluebook split layout via QuestionCard / UniversalSplitLayout.
 * Data: GET /api/math/questions (PR-D4-030). Rendering: KaTeX + NativeGraph via QuestionCard.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTest } from '../hooks/useTest';
import { QuestionCard } from '../components/QuestionCard';
import type { MathDomain, MathDifficulty } from '../lib/types';
import { resolveTelemetryUserId } from '../lib/auth-session';
import { api } from '../lib/api-client';
import '../madcrab/SatTest.css';
import '../madcrab/App.css';

function parseDomain(value: string | null): MathDomain {
    const valid: MathDomain[] = [
        'smart_practice',
        'algebra',
        'advanced_math',
        'psda',
        'geometry',
    ];
    if (value && valid.includes(value as MathDomain)) return value as MathDomain;
    return 'smart_practice';
}

function parseDifficulty(value: string | null): MathDifficulty {
    const valid: MathDifficulty[] = ['foundation', 'advanced'];
    if (value && valid.includes(value as MathDifficulty)) return value as MathDifficulty;
    return 'foundation';
}

export function MathTestPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const domain = parseDomain(searchParams.get('domain'));
    const difficulty = parseDifficulty(searchParams.get('difficulty'));
    const urlStudentId = searchParams.get('studentId');
    const studentId = resolveTelemetryUserId(urlStudentId);
    const smartTraining = searchParams.get('smart') === '1';

    const test = useTest();
    const [isFinishing, setIsFinishing] = useState(false);
    const isSubmitted = test.submittedIndexes.has(test.currentIndex);

    const handleSubmit = useCallback(() => {
        test.markSubmitted();
    }, [test]);

    const handleFinish = useCallback(async () => {
        if (isFinishing) return;
        setIsFinishing(true);
        await test.finishTest();
    }, [isFinishing, test]);

    const handleNext = useCallback(() => {
        if (test.currentIndex === test.questions.length - 1) {
            void handleFinish();
            return;
        }
        test.submitAnswer();
    }, [handleFinish, test]);

    useEffect(() => {
        const resume = searchParams.get('resume') === '1';
        if (resume) {
            void (async () => {
                try {
                    const payload = await api.getActiveProgressSession();
                    if (payload.session?.moduleType === 'math' && test.restoreServerSession(payload.session, studentId)) {
                        return;
                    }
                } catch {
                    // Fall through to dashboard if the draft cannot be restored.
                }
                navigate('/dashboard');
            })();
            return;
        }
        test.loadTest({
            mode: 'training',
            studentId,
            domain,
            difficulty,
            count: 10,
            smartTraining,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, difficulty, studentId, smartTraining]);

    useEffect(() => {
        if (test.status === 'completed') {
            navigate('/results', {
                state: {
                    questions: test.questions,
                    answers: Array.from(test.answers.entries()),
                    results: test.results,
                    score: test.score,
                    total: test.questions.length,
                    mode: test.mode,
                    sessionId: test.sessionId,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test.status]);

    if (test.status === 'loading') {
        return (
            <div className="sat-root sat-root--viewport">
                <div className="sat-loading">Loading questions…</div>
            </div>
        );
    }

    if (test.status === 'error') {
        return (
            <div className="sat-root">
                <div className="sat-error">
                    <p>
                        <strong>Could not load questions.</strong> {test.error}
                    </p>
                    <p className="sat-error__hint">
                        Math practice loads from the published PostgreSQL pool
                        (GET /api/math/questions → :4000) and does not require the Math Seed
                        generator to be online.
                    </p>
                    <button
                        type="button"
                        className="sat-topbar-badge"
                        onClick={() => navigate('/dashboard')}
                        style={{ marginTop: 16 }}
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (test.status === 'ready') {
        return (
            <div className="sat-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="app-hub-card" style={{ maxWidth: 420, margin: '2rem auto' }}>
                    <p className="app-hub-brand">SAT Generator</p>
                    <h2 className="app-hub-title">Ready to Start</h2>
                    <p className="app-hub-hint">
                        Domain: {domain.replace(/_/g, ' ')}
                        <br />
                        Difficulty: {difficulty}
                        <br />
                        Questions: {test.questions.length}
                    </p>
                    <button
                        type="button"
                        className="app-hub-primary"
                        onClick={() => void test.startTest()}
                    >
                        Begin Practice
                    </button>
                </div>
            </div>
        );
    }

    if (test.status === 'in-progress' && test.currentQuestion) {
        return (
            <div className="sat-root sat-root--viewport">
                <QuestionCard
                    question={test.currentQuestion}
                    questionNumber={test.currentIndex + 1}
                    totalQuestions={test.questions.length}
                    selectedOption={
                        test.answers.has(test.currentIndex)
                            ? test.answers.get(test.currentIndex) ?? null
                            : null
                    }
                    onSelectOption={test.selectOption}
                    onSubmit={isSubmitted ? handleNext : handleSubmit}
                    showResult={isSubmitted}
                    showFeedback
                    sectionTitle="Math"
                    onSaveDraft={async () => {
                        await test.flushSessionStep();
                    }}
                    onExit={() => {
                        navigate('/dashboard');
                    }}
                    onNavigateToQuestion={test.goToQuestion}
                    answeredQuestionIndexes={new Set(test.answers.keys())}
                />

                <footer className="sat-test__footer test-nav-bar trainer-bottom-bar">
                    <div className="bar-left flex items-center gap-4">
                        <button
                            type="button"
                            className="sat-back"
                            onClick={() => test.goToQuestion(test.currentIndex - 1)}
                            disabled={test.currentIndex === 0}
                            aria-label="Back / Назад"
                        >
                            Back
                        </button>
                    </div>
                    <div className="bar-right flex items-center gap-4">
                        {test.currentIndex === test.questions.length - 1 ? (
                            <button
                                type="button"
                                className="sat-next sat-finish-test"
                                onClick={() => void handleFinish()}
                                disabled={isFinishing}
                                aria-label="Finish Test / Завершить"
                            >
                                {isFinishing ? 'Saving…' : 'Finish Test / Завершить'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="sat-next"
                                onClick={() => test.goToQuestion(test.currentIndex + 1)}
                                aria-label="Next / Далее"
                            >
                                Next
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        );
    }

    return null;
}
